import { isShallowEqual } from "@prose-reader/shared"
import { defer, Observable, type OperatorFunction, of } from "rxjs"
import { distinctUntilChanged, first, map, switchMap } from "rxjs/operators"
import { pick } from "./objects"

export const mapKeysTo = <R extends Record<string, unknown>, K extends keyof R>(
  keys: K[],
): OperatorFunction<R, Pick<R, K>> => {
  return map((obj) => pick(obj, keys))
}

export const watchKeys =
  <R extends Record<string, unknown>, K extends keyof R>(keys: K[]) =>
  (stream: Observable<R>) => {
    return stream.pipe(mapKeysTo(keys), distinctUntilChanged(isShallowEqual))
  }

export function observeResize(
  element: HTMLElement,
): Observable<ResizeObserverEntry[]> {
  return new Observable<ResizeObserverEntry[]>((observer) => {
    // Use the observed element's own realm so observation is correct even
    // when the reader is mounted into a foreign document.
    const Ctor =
      element.ownerDocument.defaultView?.ResizeObserver ?? ResizeObserver
    const resizeObserver = new Ctor((entries) => {
      observer.next(entries)
    })

    resizeObserver.observe(element)

    return () => {
      resizeObserver.disconnect()
    }
  })
}

export const waitForSwitch =
  <O>(waitForStream: Observable<O>) =>
  <N>(stream: Observable<N>) =>
    stream.pipe(
      switchMap((value) =>
        waitForStream.pipe(
          first(),
          map(() => value),
        ),
      ),
    )

export const deferNextResult = <T>(stream: Observable<T>) => {
  let value: { result: T } | undefined

  const sub = stream.subscribe((result) => {
    value = { result: result }
  })

  return () => {
    sub.unsubscribe()

    if (value) {
      return of(value.result)
    }

    return stream
  }
}

export function idle(): Observable<void> {
  return new Observable<void>((observer) => {
    // webkit does not support requestIdleCallback yet
    if (window.requestIdleCallback) {
      const handle = window.requestIdleCallback(() => {
        observer.next()
        observer.complete()
      })

      return () => cancelIdleCallback(handle)
    }

    const timeout = setTimeout(() => {
      observer.next()
      observer.complete()
    }, 1)

    return () => clearTimeout(timeout)
  })
}

export function deferIdle<T>(callback: () => Observable<T>) {
  return defer(() => idle().pipe(switchMap(callback)))
}

export const observeMutation = (
  target: Node,
  options?: MutationObserverInit,
) => {
  return new Observable<MutationRecord[]>((subscriber) => {
    const Ctor =
      target.ownerDocument?.defaultView?.MutationObserver ?? MutationObserver
    const observer = new Ctor((mutations) => {
      subscriber.next(mutations)
    })

    observer.observe(target, options)

    return () => observer.disconnect()
  })
}

export function observeIntersection(
  element: HTMLElement,
  options?: IntersectionObserverInit,
): Observable<IntersectionObserverEntry[]> {
  return new Observable<IntersectionObserverEntry[]>((observer) => {
    const Ctor =
      element.ownerDocument.defaultView?.IntersectionObserver ??
      IntersectionObserver
    const intersectionObserver = new Ctor((entries) => {
      observer.next(entries)
    }, options)

    intersectionObserver.observe(element)

    return () => {
      intersectionObserver.disconnect()
    }
  })
}
